import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { ValueInputDialogComponent } from '@sharedModule/components/value-input-dialog/value-input-dialog.component';
import { ValueInputDialogData } from '@sharedModule/components/value-input-dialog/value-input-dialog.model';
import { ResponseIdentity } from '@sharedModule/models/server-response-identity.model';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { Observable, from, map, of, switchMap, filter } from 'rxjs';
import { SenderService } from './sender.service';
import { AML_STATUS_OPTIONS, KYC_STATUS_OPTIONS } from '../types/sender-action';

/**
 * Encapsulates the sender admin actions (active status, KYC/AML status, reset
 * password / PIN) so they can be triggered from both the sender-details screen
 * and the sender-list rows. Each method returns an Observable that emits `true`
 * only when the action succeeds, so callers can refresh their data.
 */
@Injectable({
  providedIn: 'root',
})
export class SenderActionsService {
  constructor(
    private dialog: MatDialog,
    private senderService: SenderService,
    private confirmationDialogService: ConfirmationDialogService,
    private messageHandlerService: MessageHandlerService
  ) {}

  toggleActiveStatus(senderId: number, isActive: boolean): Observable<boolean> {
    const activate = !isActive;
    return from(
      this.confirmationDialogService.openConfirmationDialog({
        title: `${activate ? 'Activate' : 'Deactivate'} Sender`,
        message: `Are you sure you want to ${
          activate ? 'activate' : 'deactivate'
        } this sender?`,
        submitButtonText: activate ? 'Activate' : 'Deactivate',
      })
    ).pipe(
      filter(confirmed => confirmed),
      switchMap(() =>
        this.senderService.updateSenderActiveStatus({
          sender_id: senderId,
          is_active: activate,
        })
      ),
      map(response => this.handleResult(response))
    );
  }

  /**
   * Turns the device verification requirement on/off. Unlike the other actions
   * this one also emits `false` when the user cancels, so the caller can put
   * its toggle back to where it was.
   */
  toggleDeviceVerification(
    senderId: number,
    required: boolean
  ): Observable<boolean> {
    return from(
      this.confirmationDialogService.openConfirmationDialog({
        title: 'Device Verification',
        message: `Are you sure you want to turn device verification ${
          required ? 'on' : 'off'
        } for this sender?`,
        submitButtonText: 'Confirm',
      })
    ).pipe(
      switchMap(confirmed =>
        confirmed
          ? this.senderService
              .updateSenderDeviceVerification({
                sender_id: senderId,
                is_device_verification_required: required,
              })
              .pipe(map(response => this.handleResult(response)))
          : of(false)
      )
    );
  }

  updateKycStatus(senderId: number): Observable<boolean> {
    return this.openValueDialog({
      title: 'Update KYC Status',
      label: 'KYC Status',
      inputType: 'select',
      options: KYC_STATUS_OPTIONS,
    }).pipe(
      switchMap(value =>
        this.senderService.updateSenderKycAmlStatus({
          sender_id: senderId,
          action: 'KYC',
          value,
        })
      ),
      map(response => this.handleResult(response))
    );
  }

  updateAmlStatus(senderId: number): Observable<boolean> {
    return this.openValueDialog({
      title: 'Update AML Status',
      label: 'AML Status',
      inputType: 'select',
      options: AML_STATUS_OPTIONS,
    }).pipe(
      switchMap(value =>
        this.senderService.updateSenderKycAmlStatus({
          sender_id: senderId,
          action: 'AML',
          value,
        })
      ),
      map(response => this.handleResult(response))
    );
  }

  resetPassword(senderId: number): Observable<boolean> {
    return this.openValueDialog({
      title: 'Reset Sender Password',
      label: 'New Password',
      inputType: 'password',
      submitButtonText: 'Reset',
    }).pipe(
      switchMap(value =>
        this.senderService.updateSenderPassword({
          sender_id: senderId,
          new_password: value,
        })
      ),
      map(response => this.handleResult(response))
    );
  }

  resetPin(senderId: number): Observable<boolean> {
    return this.openValueDialog({
      title: 'Reset Sender PIN',
      label: 'New PIN',
      inputType: 'number',
      submitButtonText: 'Reset',
    }).pipe(
      switchMap(value =>
        this.senderService.updateSenderPIN({ sender_id: senderId, pin: value })
      ),
      map(response => this.handleResult(response))
    );
  }

  /** Opens the value dialog, emitting only when a non-empty value is entered. */
  private openValueDialog(data: ValueInputDialogData): Observable<string> {
    return this.dialog
      .open(ValueInputDialogComponent, { width: '400px', data })
      .afterClosed()
      .pipe(
        filter(
          (value): value is string =>
            value !== undefined && value !== null && value !== ''
        )
      );
  }

  private handleResult(response: ResponseIdentity<null>): boolean {
    if (response.success) this.messageHandlerService.show(response.message);
    return response.success;
  }
}
